'use strict';

import * as THREE from 'three';
import { Vector2 } from 'three';

import { MetaDefinition, ToolName } from '../../services/metadata/meta-types';
import { AssetManager } from '../asset-manager';
import cameraManager from '../camera-manager';
import { ElementObj } from '../elements/element-obj';
import { InputHandlerInterface, mouseMovementTreshold, ToolInterface } from '../types';
import {
  KeyInputData,
  MouseButtonType,
  MouseInputData,
  TouchInputData,
} from '../utilities/input-event/input-event-data-extractor';

export abstract class Tool extends THREE.Object3D implements ToolInterface {
  mousePosition?: Vector2;
  private threshold: number;

  constructor(
    public name: ToolName,
    public inputHandler: InputHandlerInterface,
  ) {
    super();

    this.mousePosition = undefined;
    this.threshold = 6;
  }

  setInputHandler(inputHandler: InputHandlerInterface) {
    this.inputHandler = inputHandler;
  }

  getMeta(sweref = ''): MetaDefinition | undefined {
    return undefined;
  }

  onTouchStart(touchInputData: TouchInputData): boolean {
    return true;
  }
  onTouchMove(touchInputData: TouchInputData): boolean {
    return true;
  }
  onTouchEnd(touchInputData: TouchInputData): boolean {
    return true;
  }

  onDoubleClick(mouseInputData: MouseInputData) {
    return true;
  }
  /**
   * Checks if mouse moved since last mouse down event using this.mousePosition
   */
  mouseMoved(e: MouseInputData) {
    return Boolean(
      this.mousePosition && this.mousePosition.distanceTo(e.pointer.client) > mouseMovementTreshold,
    );
  }
  onClick(mouseInputData: MouseInputData) {
    this.mousePosition = mouseInputData.pointer.client;
    return true;
  }
  onMouseDown(mouseInputData: MouseInputData) {
    this.mousePosition = mouseInputData.pointer.client;
    return true;
  }
  onKeyDown(mouseInputData: KeyInputData) {
    return;
  }
  onKeyUp(keyInputData?: KeyInputData) {
    return;
  }
  onMouseMove(mouseInputData: MouseInputData) {
    return true;
  }
  onMouseIn(mouseInputData: MouseInputData) {
    return true;
  }
  onMouseOut(mouseInputData: MouseInputData) {
    return true;
  }
  onProjectProjectionUpdated() {
    return true;
  }
  onNewAssetSelection(asset: ElementObj) {
    return;
  }
  async onMouseUp(mouseInputData: MouseInputData) {
    if (this.withinThreshold(mouseInputData.pointer.delta.x, mouseInputData.pointer.delta.y)) {
      if (mouseInputData.button.type === MouseButtonType.RightButton && this.inputHandler) {
        this.inputHandler.setTool('select');
      } else {
        const camera = cameraManager().getActiveCamera();

        if (camera) {
          const asset = AssetManager.instance.pick(
            mouseInputData.pointer.position.x,
            mouseInputData.pointer.position.y,
            camera.getTHREECamera(),
          );
          AssetManager.instance.select(asset);
        }

        const selected = AssetManager.instance.getSelected();
        if (selected && this.inputHandler) {
          this.inputHandler.setTool('select');
          this.position.copy(selected.position);
        }
      }
    }

    return Promise.resolve(true);
  }

  onMouseWheel(e: MouseInputData) {
    return true;
  }

  onAnimate() {
    return;
  }

  enable() {
    return;
  }

  disable() {
    return;
  }

  isEditing(): boolean {
    return false;
  }

  showContextMenu(input: MouseInputData) {
    return false;
  }

  withinThreshold(x: number, y: number) {
    if (!this.mousePosition) {
      return false;
    }

    return (
      this.mousePosition.x - this.threshold < x &&
      x < this.mousePosition.x + this.threshold &&
      this.mousePosition.y - this.threshold < y &&
      y < this.mousePosition.y + this.threshold
    );
  }
}
